import React, {useCallback, useEffect} from 'react';
import PageTitle from '../shared/PageTitle';
import {createTeam, getOrganization} from '../../api/adminAPI';
import {Organization, Profile} from '../../interfaces';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';
import BackButton from '../shared/BackButton';
import {useParams} from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import TeamsTable from './TeamsTable';
import styles from '../../styles';
import UpdateOrgModal from './UpdateOrgModal';
import OrganizationProfilesTable from './OrganizationProfilesTable';

const AdminOrganizationDetail = () => {
  const {t} = useTranslation();
  const {organizationId} = useParams();

  const [organization, setOrganization] = React.useState<Organization | null>(
    null,
  );
  const [showAddTeamDialog, setShowAddTeamDialog] = React.useState(false);
  const [pending, setPending] = React.useState(true);
  const [addTeamName, setAddTeamName] = React.useState('');
  const [addTeamCode, setAddTeamCode] = React.useState('');
  const [addTeamTeamSessionGroupsEnabled, setAddTeamTeamSessionGroupsEnabled] =
    React.useState(false);
  const [addTeamIsPro, setAddTeamIsPro] = React.useState<boolean>(false);

  // fetch organization data
  const fetchData = useCallback(async () => {
    setPending(true);
    const org = await getOrganization(parseInt(organizationId ?? ''));
    setOrganization(org);
    setPending(false);
  }, [organizationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddTeamClick = useCallback(() => {
    setShowAddTeamDialog(true);
  }, []);

  const handleAddTeamClose = useCallback(() => {
    setShowAddTeamDialog(false);
    setAddTeamName('');
    setAddTeamCode('');
    setAddTeamTeamSessionGroupsEnabled(false);
    setAddTeamIsPro(false);
  }, []);

  const handleOnAddTeamNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddTeamName(e.target.value);
    },
    [],
  );

  const handleOnAddTeamCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddTeamCode(e.target.value);
    },
    [],
  );

  const handleOnAddTeamSessionGroupsEnabledChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddTeamTeamSessionGroupsEnabled(e.target.checked);
    },
    [],
  );

  const handleOnAddTeamIsProChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddTeamIsPro(e.target.checked);
    },
    [],
  );

  const handleAddTeamSubmit = useCallback(async () => {
    setPending(true);
    await createTeam({
      name: addTeamName,
      code: addTeamCode,
      organizationId: parseInt(organizationId ?? ''),
      teamSessionGroupsEnabled: addTeamTeamSessionGroupsEnabled,
      isPro: addTeamIsPro,
    });
    handleAddTeamClose();
    fetchData();
  }, [
    addTeamCode,
    addTeamIsPro,
    addTeamName,
    addTeamTeamSessionGroupsEnabled,
    fetchData,
    handleAddTeamClose,
    organizationId,
  ]);

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {
            pageName: t('pages.organizationDetail', {
              organizationName: organization?.name,
            }),
          })}
        </title>
      </Helmet>
      <Box>
        <PageTitle>
          {t('pages.organizationDetail', {
            organizationName: organization?.name,
          })}
        </PageTitle>
        <BackButton navigationUrl="/admin/organizations">
          {t('pages.organizations')}
        </BackButton>
        <Typography>
          {t('admin.country')}: {organization?.country ?? '—'}
        </Typography>
        <Typography>
          {t('admin.stateOrProvince')}: {organization?.stateOrProvince ?? '—'}
        </Typography>
        <Typography sx={{mb: 2}}>
          {t('admin.hideLeaderboard')}:{' '}
          {organization?.hideLeaderboard ? t('general.yes') : t('general.no')}
        </Typography>
        {organization ? (
          <UpdateOrgModal
            organization={organization}
            loading={pending}
            onUpdate={fetchData}
          />
        ) : (
          <Button sx={{mb: 2, mr: 2}} disabled={true}>
            {t('admin.updateOrganization')}
          </Button>
        )}
        <Button sx={{mb: 2}} onClick={handleAddTeamClick} disabled={pending}>
          {t('admin.addTeam')}
        </Button>
        <Modal
          open={showAddTeamDialog}
          onClose={handleAddTeamClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box sx={styles.modal}>
            <Typography variant="h4" sx={{mb: 2}}>
              {t('admin.addTeam')}
            </Typography>
            <FormGroup>
              <TextField
                label={t('general.name')}
                name="name"
                variant="outlined"
                value={addTeamName}
                onChange={handleOnAddTeamNameChange}
                sx={{mb: 2}}
                fullWidth
              />
              <TextField
                label={t('admin.code')}
                name="code"
                variant="outlined"
                value={addTeamCode}
                onChange={handleOnAddTeamCodeChange}
                sx={{mb: 2}}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="teamSessionGroupsEnabled"
                    value={addTeamTeamSessionGroupsEnabled}
                    onChange={handleOnAddTeamSessionGroupsEnabledChange}
                  />
                }
                label={t('admin.teamSessionGroupsEnabled')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="isPro"
                    value={addTeamIsPro}
                    onChange={handleOnAddTeamIsProChange}
                  />
                }
                label={t('admin.isPro')}
              />
              <Button
                disabled={pending || addTeamName.length === 0}
                onClick={handleAddTeamSubmit}>
                {t('general.submit')}
              </Button>
            </FormGroup>
          </Box>
        </Modal>
        <Typography variant="h4" sx={{my: 2}}>
          {t('admin.teams')}
        </Typography>
        <TeamsTable teams={organization?.teams ?? []} height={300} />
        <OrganizationProfilesTable
          individualProfiles={organization?.profiles ?? []}
          teamProfiles={
            (organization?.teams ?? []).flatMap(team =>
              (team.players ?? [])
                .filter(player => player?.profile)
                .map(player => player?.profile as Profile),
            ) ?? []
          }
        />
      </Box>
    </>
  );
};

export default AdminOrganizationDetail;
